'use client';

import { useState } from 'react';

import { HiMiniPause, HiOutlineSparkles, HiSparkles } from 'react-icons/hi2';
import {
  IoBookmark,
  IoBookmarkOutline,
  IoBugOutline,
  IoBuildOutline,
  IoClipboardOutline,
  IoEnterOutline,
  IoFlag,
  IoFlagOutline,
  IoWarningOutline,
} from 'react-icons/io5';

import { useSanitizedHtml } from '../../../utils/sanitize';
import { IconButton } from '../../icon-button';
import {
  IconTooltip,
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from '../../tooltip';
import type { InstructionsType } from '../Question.types';
import { QuestionImage } from './QuestionImage';

import styles from './QuestionControls.module.scss';

/**
 * The controls a question header can carry beyond the ones every screen has. Each is a
 * whole control rather than a prop the header has to learn, so a screen composes the ones
 * it wants into the header's slots and the header stays the same size as screens are
 * added.
 */

type BookmarkButtonProps = {
  bookmarked?: boolean;
  /** Left out to grey the button rather than remove it, so the header keeps its shape. */
  onToggle?: () => void;
};

function BookmarkButton({ bookmarked, onToggle }: BookmarkButtonProps) {
  return (
    <IconButton
      icon={<IoBookmarkOutline />}
      flipped={bookmarked}
      flipIcon={<IoBookmark />}
      aria-label={bookmarked ? 'Quitar marcador' : 'Marcar pregunta'}
      onClick={onToggle}
      disabled={onToggle === undefined}
    />
  );
}

type FlagButtonProps = {
  flagged?: boolean;
  onFlag: () => void;
};

function FlagButton({ flagged, onFlag }: FlagButtonProps) {
  return (
    <IconButton
      className={styles['flag']}
      icon={<IoFlagOutline />}
      flipped={flagged}
      flipIcon={<IoFlag />}
      aria-label={flagged ? 'Quitar incidencia' : 'Reportar incidencia'}
      onClick={onFlag}
    />
  );
}

type PauseButtonProps = {
  /**
   * Greys the button out instead of removing it, so it keeps its place in the header
   * while the attempt is already paused, syncing, or finished.
   */
  disabled?: boolean;
  onPause: () => void;
};

function PauseButton({ disabled = false, onPause }: PauseButtonProps) {
  return (
    <IconButton
      icon={<HiMiniPause />}
      aria-label="Pausar"
      onClick={onPause}
      disabled={disabled}
      size="sm"
    />
  );
}

type ExplanationButtonProps = {
  shown?: boolean;
  onToggle: () => void;
};

function ExplanationButton({
  shown = false,
  onToggle,
}: ExplanationButtonProps) {
  return (
    <IconButton
      className={styles['explanation']}
      icon={<HiOutlineSparkles />}
      flipped={shown}
      flipIcon={<HiSparkles />}
      aria-label={shown ? 'Ocultar explicación' : 'Ver explicación'}
      onClick={onToggle}
    />
  );
}

function FeedbackButton({ onFeedback }: { onFeedback: () => void }) {
  return (
    <IconButton
      icon={<IoBugOutline />}
      aria-label="Enviar comentarios"
      onClick={onFeedback}
    />
  );
}

function PushQuestionButton({ onPush }: { onPush: () => void }) {
  return (
    <IconButton
      icon={<IoEnterOutline />}
      aria-label="Enviar pregunta"
      onClick={onPush}
    />
  );
}

function EditQuestionButton({ onEdit }: { onEdit: () => void }) {
  return (
    <IconButton
      icon={<IoBuildOutline />}
      aria-label="Editar pregunta"
      onClick={onEdit}
    />
  );
}

/** Says the content below it was written by a machine, wherever one wrote it. */
function AiWarningIcon() {
  return (
    <IconTooltip
      icon={<IoWarningOutline />}
      label="Aviso"
      size="sm"
      className={styles['warning']}
      contentClassName={styles['warning-content']}
    >
      Este contenido ha sido generado con inteligencia artificial, verifica
      siempre su exactitud.
    </IconTooltip>
  );
}

/**
 * Opens the sitting's instructions on click rather than on hover: they are long enough to
 * want reading, and a hover tooltip goes as soon as the pointer leaves it.
 */
function InstructionsTooltip({
  instructions,
}: {
  instructions: InstructionsType;
}) {
  const [open, setOpen] = useState(false);
  const html = useSanitizedHtml(instructions.text);

  return (
    <Tooltip placement="bottom" open={open} onOpenChange={setOpen}>
      <TooltipTrigger asChild={true}>
        <IconButton
          icon={<IoClipboardOutline />}
          aria-label="Ver instrucciones"
          onClick={() => setOpen((v) => !v)}
        />
      </TooltipTrigger>
      <TooltipContent className={styles['instructions']}>
        <div className={styles['text']} dangerouslySetInnerHTML={html} />
        {instructions.imageUrl && (
          <QuestionImage
            src={instructions.imageUrl}
            alt="Imagen de las instrucciones"
          />
        )}
      </TooltipContent>
    </Tooltip>
  );
}

export {
  AiWarningIcon,
  BookmarkButton,
  EditQuestionButton,
  ExplanationButton,
  FeedbackButton,
  FlagButton,
  InstructionsTooltip,
  PauseButton,
  PushQuestionButton,
};
